// Builds the personal/family-history and cardiology clinical ReviewSections shown on the
// Invitae order review page (PD-636 C3.8). Pure functions over the form state so the
// component stays declarative.

import type { InvitaeFormState, FamilyHistoryRow, CancerHistoryEntry } from './formData'
import { CANCER_TYPES } from './formData'
import type { CardiacClinicalHistory } from './cardiologyFormData'
import { CLINICAL_DIAGNOSES, CLINICAL_INFO_CATEGORIES } from './cardiologyFormData'
import type { ReviewSection } from './shared/OrderReviewSummary'

type Item = { label: string; value: string }

const yesNo = (v: string): string =>
  v === 'yes' ? 'Yes' : v === 'no' ? 'No' : v === 'unknown' ? 'Unknown' : ''

function cancerPersonalHistory(form: InvitaeFormState): ReviewSection | null {
  if (form.noPersonalHistory) {
    return {
      title: 'Personal History of Cancer',
      items: [{ label: 'History', value: 'No personal history of cancer' }],
    }
  }
  const items: Item[] = form.cancerHistory.map((entry: CancerHistoryEntry) => {
    const label = CANCER_TYPES.find((c) => c.value === entry.type)?.label ?? entry.type
    const parts: string[] = []
    if (entry.ageOfDiagnosis) parts.push(`Age ${entry.ageOfDiagnosis}`)
    if (entry.breastOptions.length) parts.push(entry.breastOptions.join(', '))
    if (entry.msiHighOrIhcAbnormal)
      parts.push(`MSI-high/IHC abnormal${entry.msiResult ? `: ${entry.msiResult}` : ''}`)
    if (entry.additionalInfo) parts.push(entry.additionalInfo)
    if (entry.metastatic) parts.push(`Metastatic: ${yesNo(entry.metastatic)}`)
    if (entry.gleasonScore) parts.push(`Gleason ${entry.gleasonScore}`)
    if (entry.polypCount) parts.push(`Polyps ${entry.polypCount}`)
    if (entry.pathology) parts.push(`Pathology: ${entry.pathology}`)
    if (entry.cancerType) parts.push(entry.cancerType)
    return { label, value: parts.join(' — ') }
  })
  return items.length ? { title: 'Personal History of Cancer', items } : null
}

function familyHistory(form: InvitaeFormState, title: string): ReviewSection | null {
  const items: Item[] = []
  if (form.patientIsAdopted) items.push({ label: 'Adopted', value: 'Patient is adopted' })
  if (form.noFamilyHistory) {
    items.push({ label: 'History', value: 'No known family history' })
  } else {
    form.familyHistory.forEach((row: FamilyHistoryRow, i) => {
      const parts = [
        row.condition,
        row.maternalPaternal,
        row.ageAtDiagnosis ? `age ${row.ageAtDiagnosis}` : '',
        row.additionalDetails,
      ].filter(Boolean)
      if (row.relationship || parts.length) {
        items.push({ label: row.relationship || `Relative ${i + 1}`, value: parts.join(' — ') })
      }
    })
  }
  return items.length ? { title, items } : null
}

function cardiacPersonalHistory(cardiac: CardiacClinicalHistory): ReviewSection | null {
  const items: Item[] = []
  if (cardiac.isAffected) {
    const age = cardiac.ageAtDiagnosis ? ` (age ${cardiac.ageAtDiagnosis})` : ''
    items.push({ label: 'Affected/symptomatic', value: `${yesNo(cardiac.isAffected)}${age}` })
  }
  if (cardiac.briefDescription)
    items.push({ label: 'Description', value: cardiac.briefDescription })
  if (cardiac.hasHematologicalMalignancy)
    items.push({
      label: 'Hematological malignancy',
      value: yesNo(cardiac.hasHematologicalMalignancy),
    })
  if (cardiac.hasPriorGeneticTesting)
    items.push({ label: 'Prior genetic testing', value: yesNo(cardiac.hasPriorGeneticTesting) })
  if (cardiac.priorGeneticTestResults)
    items.push({ label: 'Prior test results', value: cardiac.priorGeneticTestResults })
  return items.length ? { title: 'Personal History', items } : null
}

function clinicalDiagnosis(cardiac: CardiacClinicalHistory): ReviewSection | null {
  const labels = cardiac.clinicalDiagnoses.map((v) => {
    let label = CLINICAL_DIAGNOSES.find((d) => d.value === v)?.label ?? v
    if (v === 'taad' && cardiac.taadDetails) label += `: ${cardiac.taadDetails}`
    if (v === 'other' && cardiac.clinicalDiagnosisOther)
      label += `: ${cardiac.clinicalDiagnosisOther}`
    return label
  })
  return labels.length
    ? { title: 'Clinical Diagnosis', items: [{ label: 'Diagnoses', value: labels.join('; ') }] }
    : null
}

function clinicalChecklist(cardiac: CardiacClinicalHistory): ReviewSection | null {
  const items: Item[] = []
  for (const cat of CLINICAL_INFO_CATEGORIES) {
    const status = cardiac.categoryStatus[cat.category] || ''
    const selected = cardiac.selectedItems[cat.category] || []
    if (!status && selected.length === 0) continue
    const itemLabels = selected.map((iv) => {
      const item = cat.items.find((i) => i.value === iv)
      let label = item?.label ?? iv
      const text = cardiac.textFields[iv]
      if (text) label += `: ${text}`
      const subs = cardiac.subSelections[iv] || []
      if (subs.length && item?.subItems) {
        const subLabels = subs.map((sv) => item.subItems!.find((s) => s.value === sv)?.label ?? sv)
        label += ` (${subLabels.join(', ')})`
      }
      return label
    })
    const parts = [status ? yesNo(status) : '', itemLabels.join('; ')].filter(Boolean)
    items.push({ label: cat.category, value: parts.join(' — ') })
  }
  return items.length ? { title: 'Clinical Information Checklist', items } : null
}

/** Personal/family history + cardiology clinical sections for the Invitae review page. */
export function buildInvitaeHistorySections(form: InvitaeFormState): ReviewSection[] {
  const sections: ReviewSection[] = []
  if (form.formType === 'cancer') {
    const ph = cancerPersonalHistory(form)
    if (ph) sections.push(ph)
    const fh = familyHistory(form, 'Family History of Cancer')
    if (fh) sections.push(fh)
    return sections
  }

  const cardiac = form.cardiacHistory
  if (cardiac) {
    const ph = cardiacPersonalHistory(cardiac)
    if (ph) sections.push(ph)
  }
  const fh = familyHistory(form, 'Family History')
  if (fh) sections.push(fh)
  if (form.requestedVariants.trim()) {
    sections.push({
      title: 'Requested Variants',
      items: [{ label: 'Gene(s)/Variant(s)', value: form.requestedVariants }],
    })
  }
  if (cardiac) {
    const cd = clinicalDiagnosis(cardiac)
    if (cd) sections.push(cd)
    const cc = clinicalChecklist(cardiac)
    if (cc) sections.push(cc)
  }
  return sections
}
